import React, {useState} from 'react';
import {
  Box,
  Button,
  Image,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Center,
  VStack,
} from '@chakra-ui/react';
import {motion, useMotionValue, animate} from 'framer-motion';
import Confetti from 'react-confetti';
import {useTranslate} from '@eksab/i18n';
import {useSpinWheelOffers, useSpinWheelMutation} from './hooks/useSpinWheel';
import type {SpinWheelOffer} from './types';
import {RemoveAppHeader, RemoveAppFooter, MobileNav} from '@eksab/components';
import CloseIframeButton from '@eksab/components/CloseIframeButton';

interface SpinWheelGameProps {
  gameCode: string;
}

export const SpinWheelGame: React.FC<SpinWheelGameProps> = ({gameCode}) => {
  const t = useTranslate();
  const {data: offers, isLoading} = useSpinWheelOffers(gameCode);
  const spinMutation = useSpinWheelMutation(gameCode);

  // Use MotionValue for continuous rotation control
  const rotation = useMotionValue(0);

  const {isOpen: isResultOpen, onOpen: onResultOpen, onClose: onResultClose} = useDisclosure();
  const {isOpen: isTcOpen, onOpen: onTcOpen, onClose: onTcClose} = useDisclosure();
  const {isOpen: isErrorOpen, onOpen: onErrorOpen, onClose: onErrorClose} = useDisclosure();
  const [result, setResult] = useState<SpinWheelOffer | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSpinning, setIsSpinning] = useState(false);

  const spin = async () => {
    if (isSpinning || !offers) return;

    setIsSpinning(true);

    // 1. Start the wheel spinning immediately (fast linear spin)
    const currentRotation = rotation.get();
    const spinAnimation = animate(rotation, currentRotation + 36000, {
      duration: 60, // slightly slower initial spin
      ease: 'linear',
      repeat: Infinity,
    });

    try {
      // 2. Fetch the result while spinning
      const offer = await spinMutation.mutateAsync();
      const offerIndex = offers.findIndex((o: SpinWheelOffer) => o.id === offer.id);

      if (offerIndex === -1) {
        spinAnimation.stop();
        setIsSpinning(false);
        return;
      }

      // 3. Calculate target rotation
      spinAnimation.stop();

      const current = rotation.get();
      const anglePerSegment = 360 / offers.length;
      const winnerCenterAngle = offerIndex * anglePerSegment + anglePerSegment / 2;
      const targetBase = 360 - winnerCenterAngle;

      const minSpins = 5;
      const currentMod = current % 360;
      const targetMod = (targetBase + 36000) % 360;
      let diff = targetMod - currentMod;
      if (diff < 0) diff += 360;

      const finalTarget = current + 360 * minSpins + diff;

      // 4. Animate to the specific target using a deceleration curve
      // In framer-motion v7, animate() returns controls, not a promise.
      // We wrap it in a Promise to ensure it finishes before showing the modal.
      await new Promise<void>((resolve) => {
        animate(rotation, finalTarget, {
          duration: 6,
          ease: 'circOut',
          onComplete: () => resolve(),
        });
      });

      // Small delay so the user sees where it stopped
      await new Promise((resolve) => setTimeout(resolve, 800));

      setResult(offer);
      setIsSpinning(false);
      onResultOpen();

      // Notify owner of iframe
      window.parent?.postMessage({event: 'spin-wheel.offer-received', payload: {offer}}, '*');
    } catch (error: any) {
      console.error('Spin failed', error);
      spinAnimation.stop();
      setIsSpinning(false);

      const message = error?.response?.data?.message || error?.message || t('app.errors.try-again');
      setErrorMessage(message);
      onErrorOpen();
    }
  };

  const handleConfirm = () => {
    onResultClose();
    window.parent?.postMessage({event: 'spin-wheel.confirmed', payload: {offer: result}}, '*');
  };

  if (isLoading)
    return (
      <Center h="100vh">
        <Text>{t('app.state.loading')}</Text>
      </Center>
    );

  return (
    <Box position="relative" minH="100vh" bg="gray.50" overflow="hidden">
      <RemoveAppHeader />
      <RemoveAppFooter />
      <MobileNav />
      <CloseIframeButton event="spin-wheel.closed" />

      {isResultOpen && <Confetti recycle={false} numberOfPieces={200} />}

      <VStack spacing={8} pt={20} pb={10} px={4} align="center">
        <Text fontSize="2xl" fontWeight="800" textAlign="center" color="#5c2d91 !important">
          {t('spin_wheel.title', {defaultValue: 'Spin & Win!'})}
        </Text>

        <Box position="relative" w={['300px', '400px']} h={['300px', '400px']}>
          {/* Wheel Pointer - Improved Design */}
          <Box
            position="absolute"
            top="-24px"
            left="50%"
            transform="translateX(-50%)"
            zIndex={2}
            filter="drop-shadow(0px 2px 2px rgba(0,0,0,0.3))"
          >
            <svg width="40" height="50" viewBox="0 0 40 50">
              <path d="M 20 50 L 5 10 L 10 0 L 30 0 L 35 10 Z" fill="#E0E0E0" stroke="#BDBDBD" strokeWidth="1" />
              <path d="M 20 50 L 10 10 L 30 10 Z" fill="#5c2d91" />
            </svg>
          </Box>

          {/* The Wheel */}
          <motion.div
            style={{width: '100%', height: '100%', position: 'relative', rotate: rotation, willChange: 'transform'}}
          >
            <svg viewBox="0 0 100 100" style={{width: '100%', height: '100%', overflow: 'visible'}}>
              <defs>
                <linearGradient id="rim-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#E0E0E0" />
                  <stop offset="50%" stopColor="#FFFFFF" />
                  <stop offset="100%" stopColor="#BDBDBD" />
                </linearGradient>
                <radialGradient id="center-gradient">
                  <stop offset="0%" stopColor="#F5F5F5" />
                  <stop offset="100%" stopColor="#BDBDBD" />
                </radialGradient>
                <filter id="shadow">
                  <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="rgba(0,0,0,0.3)" />
                </filter>
                <filter id="inner-shadow">
                  <feComponentTransfer in="SourceAlpha">
                    <feFuncA type="table" tableValues="1 0" />
                  </feComponentTransfer>
                  <feGaussianBlur stdDeviation="2" />
                  <feOffset dx="0" dy="0" result="offsetblur" />
                  <feFlood floodColor="rgba(0,0,0,0.5)" result="color" />
                  <feComposite in2="offsetblur" operator="in" />
                  <feComposite in2="SourceAlpha" operator="in" />
                  <feMerge>
                    <feMergeNode in="SourceGraphic" />
                    <feMergeNode />
                  </feMerge>
                </filter>
              </defs>

              {/* Outer Rim */}
              <circle cx="50" cy="50" r="49" fill="url(#rim-gradient)" filter="url(#shadow)" />
              <circle cx="50" cy="50" r="46" fill="#2d1646" />

              {/* Segments */}
              {offers?.map((offer: SpinWheelOffer, i: number) => {
                const angle = 360 / offers.length;
                const startAngle = i * angle;
                const endAngle = (i + 1) * angle;

                // Adjust radius to fit inside the rim
                const r = 46;
                const x1 = 50 + r * Math.cos((Math.PI * (startAngle - 90)) / 180);
                const y1 = 50 + r * Math.sin((Math.PI * (startAngle - 90)) / 180);
                const x2 = 50 + r * Math.cos((Math.PI * (endAngle - 90)) / 180);
                const y2 = 50 + r * Math.sin((Math.PI * (endAngle - 90)) / 180);

                const data = `M 50 50 L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`;

                // Colors derived from correct usage of #5c2d91
                const colors = ['#7A5AA5', '#9C85BD', '#C6BBDB'];
                let colorIndex = i % colors.length;

                // If last segment matches the first one (wrap-around conflict)
                if (i === offers.length - 1 && colorIndex === 0 && offers.length > 1) {
                  colorIndex = 1;
                }

                const fillColor = colors[colorIndex];
                const textColor = colorIndex === 2 ? '#5c2d91' : '#FFFFFF';

                const words = offer.name.split(' ');
                const isMultiLine = words.length > 1;
                const line1 = isMultiLine ? words.slice(0, Math.ceil(words.length / 2)).join(' ') : offer.name;
                const line2 = isMultiLine ? words.slice(Math.ceil(words.length / 2)).join(' ') : '';

                return (
                  <g key={offer.id}>
                    <path d={data} fill={fillColor} stroke="#FFFFFF" strokeWidth="0.5" />
                    <text
                      x="22"
                      y="50"
                      fontSize={isMultiLine ? '4.5' : '5.5'}
                      fontWeight="800"
                      fill={textColor}
                      transform={`rotate(${startAngle + angle / 2 + 90} 50 50)`}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{pointerEvents: 'none', fontFamily: 'Cairo, sans-serif'}}
                    >
                      {isMultiLine ? (
                        <>
                          <tspan x="22" dy="-0.6em">
                            {line1}
                          </tspan>
                          <tspan x="22" dy="1.2em">
                            {line2}
                          </tspan>
                        </>
                      ) : (
                        offer.name
                      )}
                    </text>
                  </g>
                );
              })}

              {/* Center Hub */}
              <circle cx="50" cy="50" r="8" fill="url(#rim-gradient)" stroke="#9E9E9E" strokeWidth="1" />
              <circle cx="50" cy="50" r="5" fill="url(#center-gradient)" />

              {/* Decorative Bolts on Rim (Optional but adds detail) */}
              {[...Array(8)].map((_, i) => (
                <circle
                  key={i}
                  cx={50 + 47.5 * Math.cos((Math.PI * (i * 45 - 90)) / 180)}
                  cy={50 + 47.5 * Math.sin((Math.PI * (i * 45 - 90)) / 180)}
                  r="1"
                  fill="#FFF"
                />
              ))}
            </svg>
          </motion.div>
        </Box>

        <Button
          size="lg"
          backgroundColor="#5c2d91 !important"
          color="white !important"
          onClick={spin}
          isLoading={isSpinning}
          loadingText={t('spin_wheel.spinning', {defaultValue: 'Spinning...'})}
          rounded="full"
          px={16}
          py={8}
          fontSize="2xl"
          fontWeight="900"
          boxShadow="0 6px 20px 0 rgba(92, 45, 145, 0.5)"
          _hover={{transform: 'scale(1.05)', backgroundColor: '#4a2475 !important'}}
          _active={{transform: 'scale(0.95)', backgroundColor: '#381b59 !important'}}
        >
          {t('spin_wheel.spin_button', {defaultValue: 'Spin'})}
        </Button>

        <Button variant="link" size="sm" color="#5c2d91" onClick={onTcOpen}>
          {t('app.terms_and_conditions')}
        </Button>
      </VStack>

      {/* Result Modal */}
      <Modal isOpen={isResultOpen} onClose={onResultClose} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent rounded="3xl">
          <ModalHeader textAlign="center" fontSize="3xl" fontWeight="900" color="#5c2d91" pt={8}>
            مبروووك!!
          </ModalHeader>
          <ModalBody pb={10}>
            <VStack spacing={6}>
              {result?.image && (
                <Box p={4} bg="purple.50" rounded="2xl" border="2px dashed" borderColor="purple.200">
                  <Image src={result.image} alt={result.name} w="120px" h="120px" objectFit="contain" />
                </Box>
              )}
              <Text fontSize="2xl" fontWeight="800" color="#2d1646" textAlign="center" lineHeight="shorter">
                كسبت {result?.name}
              </Text>
              <Button
                w="full"
                h="60px"
                fontSize="xl"
                fontWeight="bold"
                backgroundColor="#5c2d91 !important"
                color="white !important"
                onClick={handleConfirm}
                rounded="2xl"
                boxShadow="0 4px 12px rgba(92, 45, 145, 0.3)"
                _hover={{backgroundColor: '#4a2475 !important', transform: 'translateY(-2px)'}}
                _active={{transform: 'translateY(0)'}}
              >
                Ok
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* T&C Modal */}
      <Modal isOpen={isTcOpen} onClose={onTcClose} isCentered size="lg" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent rounded="xl">
          <ModalHeader>{t('app.terms_and_conditions')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text whiteSpace="pre-wrap" dir="rtl" textAlign="right">
              {t('spin_wheel.terms_content')}
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
      {/* Error Modal */}
      <Modal isOpen={isErrorOpen} onClose={onErrorClose} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent rounded="3xl" p={4}>
          <ModalHeader textAlign="center" fontSize="2xl" fontWeight="800" color="red.500">
            {t('app.errors.error-occured', {defaultValue: 'خطأ!'})}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={6}>
              <Text textAlign="center" fontSize="lg" fontWeight="600" color="gray.500">
                {errorMessage}
              </Text>
              <Button
                w="full"
                h="56px"
                backgroundColor="#5c2d91 !important"
                color="white !important"
                onClick={onErrorClose}
                rounded="2xl"
                fontSize="lg"
                fontWeight="bold"
                _hover={{backgroundColor: '#4a2475 !important'}}
              >
                {t('app.actions.try-again', {defaultValue: 'حاول مجددًا'})}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
